import React, { useState, useEffect, useMemo, useCallback } from "react";

import { SolutionsFrame } from "components/solutions/SolutionsFrame";
import { BlockStandard } from "components/admin/BlockStandard";
import { Button, Grid, TextField, Typography } from "@mui/material";
import { SpaceSelector } from "components/admin/SpaceSelector";
import { DataTable } from "components/dashboard/DataTable";
import { useToken, useUserContext } from "contexts/UserContext";
import { fetchSimulatorData } from "api/simulator";
import { EconomyByType, EconomyParams } from "api/simulatorTypes";
import { useSetError, useErrorInfo } from "contexts/ErrorContext";
import { CustomCodes } from "contexts/ErrorContextData";

const FRAME_DATA = {
  title: "Simulador Enermatch",
};

const formatTableData = (dataType: EconomyParams | undefined) => {
  const investment =
    dataType?.initialInvestment || dataType?.initialInvestment === 0
      ? dataType.initialInvestment.toLocaleString("pt-br")
      : "-";
  const economy =
    dataType?.monthlyEconomyValue && dataType?.monthlyEconomyPercent
      ? `${dataType.monthlyEconomyValue.toLocaleString("pt-br", {
          style: "currency",
          currency: "brl",
        })} / ${dataType.monthlyEconomyPercent.toLocaleString("pt-br")}%`
      : "-";
  const footprint = dataType?.co2ReductionValue
    ? `${dataType.co2ReductionPercent.toLocaleString("pt-br")} kgCO2e`
    : "-";
  return [
    {
      key: "Investimento inicial",
      value: investment === "0" ? "ZERO" : investment,
      StartIcon: <img src="/static/icons/investment.svg" alt="" />,
    },
    {
      key: "Economia estimada mensal",
      value: economy,
      StartIcon: <img src="/static/icons/coin-hand.svg" alt="" />,
    },
    {
      key: "Redução da Pegada Carbônica",
      value: footprint,
      StartIcon: <img src="/static/icons/leaf.svg" alt="" />,
    },
  ];
};

const CARD_STYLE = {
  alignItems: "center",
  justifyContent: "space-evenly",
  padding: "16px 8px",
  marginBottom: "8px",
  borderRadius: "12px",
  border: "1px solid #CAC4D0",
};

export const SimulatorResults = ({ history, location }) => {
  const token = useToken();
  const isNewSpace = useMemo(
    () => location.state.newSpace,
    [location.state.newSpace]
  );
  const { currentSpace } = useUserContext();
  const hasPropertiesData = useMemo(
    () => !!currentSpace?.properties?.length,
    [currentSpace]
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [monthlyEnergyCost, setMonthlyEnergyCost] = useState<string>(
    // @ts-expect-error the check cover this case
    hasPropertiesData ? currentSpace.properties[0].monthlyEnergyCost : ""
  );
  const [simulationData, setSimulationData] = useState<
    EconomyByType | undefined
  >();

  const [greenEnergyTable, airConditioningTable] = useMemo(() => {
    const greenEnergyData = formatTableData(simulationData?.greenEnergy);
    const airConData = formatTableData(simulationData?.airConditioning);
    return [greenEnergyData, airConData];
  }, [simulationData]);
  const setGlobalError = useSetError();
  const errorInfo = useErrorInfo();
  const hasFormError = errorInfo?.code === CustomCodes.INCOMPLETE_FORM;

  const loadSimulatorData = useCallback(
    async (
      params:
        | { propertyId: string }
        | { monthlyEnergyCost: string; voltageLevel: string }
    ) => {
      setIsLoading(true);
      setSimulationData(undefined);
      try {
        const data = await fetchSimulatorData({ token, ...params });
        setSimulationData(data);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    },
    [setGlobalError, token]
  );

  useEffect(() => {
    // update the values every time currentSpace changes ONLY if we're not simulating for a new building
    // and the current building has the required properties
    if (!isNewSpace && currentSpace?.properties?.length) {
      setMonthlyEnergyCost(currentSpace?.properties[0].monthlyEnergyCost);
      loadSimulatorData({ propertyId: currentSpace?.properties[0].id });
    } else {
      // clear the form if the user changes the currentSpace
      setMonthlyEnergyCost("");
    }
  }, [currentSpace, isNewSpace, loadSimulatorData, setMonthlyEnergyCost]);

  const handleSubmit = () => {
    if (monthlyEnergyCost) {
      const voltageLevel = +monthlyEnergyCost > 10000 ? "HIGH" : "MEDIUM";
      loadSimulatorData({ monthlyEnergyCost, voltageLevel });
    } else {
      setGlobalError(CustomCodes.INCOMPLETE_FORM);
    }
  };

  return (
    <SolutionsFrame {...FRAME_DATA}>
      {!location.state.newSpace && <SpaceSelector />}
      <BlockStandard hideDivider>
        <Grid item container direction="row" gap={1}>
          <TextField
            value={monthlyEnergyCost}
            onChange={({ target }) => setMonthlyEnergyCost(target.value)}
            label="Insira seu custo mensal com energia"
            placeholder={`R$  / mês`}
            sx={{ flex: 1 }}
            error={hasFormError}
            helperText={
              hasFormError
                ? "O preenchimento do custo mensal é obrigatório"
                : ""
            }
          />
          <Button variant="contained" onClick={handleSubmit}>
            Simular
          </Button>
        </Grid>
      </BlockStandard>

      <BlockStandard hideDivider>
        <Grid item container sx={CARD_STYLE}>
          <img
            alt=""
            src="/static/img/solutions/green-energy.png"
            width="100px"
          />
          <Typography variant="body2">Contrato energia verde</Typography>

          <BlockStandard hideDivider>
            <DataTable
              detailTable
              data={greenEnergyTable}
              isLoading={isLoading}
            />
          </BlockStandard>

          <Button
            variant="contained"
            fullWidth
            onClick={() => history.push("green-energy-contracts")}
          >
            Contratar
          </Button>
        </Grid>

        <Grid item container sx={CARD_STYLE}>
          <img
            alt=""
            src="/static/img/solutions/automate-ac.png"
            width="100px"
          />
          <Typography variant="body2">Automação de ar condicionado</Typography>

          <BlockStandard hideDivider>
            <DataTable
              detailTable
              isLoading={isLoading}
              data={airConditioningTable}
            />
          </BlockStandard>

          <Button
            variant="contained"
            fullWidth
            onClick={() => history.push("automate-ac")}
          >
            Contratar
          </Button>
        </Grid>
      </BlockStandard>
    </SolutionsFrame>
  );
};
